import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import type { MediaType, Movie, NewsItem } from "@/types/media";

export const allMovies = movies as unknown as Movie[];
export const allNews = newsList as unknown as NewsItem[];

export const TYPE_LABELS: Record<MediaType, string> = {
  movie: "电影",
  tv: "电视剧",
  anime: "动漫",
  variety: "综艺",
};

export const CATEGORY_TYPES: MediaType[] = ["movie", "tv", "anime", "variety"];

export const REGIONS = ["中国", "美国", "韩国", "日本", "英国"];

export const GENRES = [
  "科幻",
  "冒险",
  "剧情",
  "古装",
  "犯罪",
  "悬疑",
  "惊悚",
  "家庭",
  "动作",
  "赛车",
  "爱情",
  "奇幻",
  "史诗",
  "喜剧",
  "美食",
  "年代",
  "都市",
  "情感",
  "刑侦",
  "商战",
  "历史",
  "玄幻",
  "热血",
  "音乐",
  "综艺",
  "真人秀",
];

export const YEARS = [2025, 2024, 2023];

export type SortKey = "latest" | "popular" | "rating";

export const SORT_LABELS: Record<SortKey, string> = {
  latest: "按更新时间",
  popular: "按热门程度",
  rating: "按评分高低",
};

export function typeLabel(type: string): string {
  return TYPE_LABELS[type as MediaType] ?? "影视";
}

export function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function getMovieById(id: string): Movie | undefined {
  return allMovies.find((item) => item.id === id);
}

export function getMoviesByType(type: string): Movie[] {
  return allMovies.filter((item) => item.type === type);
}

export interface MovieFilter {
  type?: string;
  region?: string;
  year?: number;
  genre?: string;
  keyword?: string;
}

export function filterMovies(filter: MovieFilter): Movie[] {
  const keyword = filter.keyword?.trim().toLowerCase();
  return allMovies.filter((item) => {
    if (filter.type && item.type !== filter.type) return false;
    if (filter.region && item.region !== filter.region) return false;
    if (filter.year && item.year !== filter.year) return false;
    if (filter.genre && !item.genres.includes(filter.genre)) return false;
    if (keyword) {
      const haystack = `${item.title} ${item.director} ${item.cast.join(" ")} ${item.genres.join(" ")}`.toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    return true;
  });
}

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const copy = [...list];
  if (sort === "popular") {
    return copy.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return copy.sort((a, b) => b.rating - a.rating);
  }
  return copy.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
}

export function paginate<T>(list: T[], page: number, pageSize: number): Paginated<T> {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    page: safePage,
    totalPages,
  };
}

export function getLatestMovies(count: number, type?: string): Movie[] {
  const base = type ? getMoviesByType(type) : allMovies;
  return sortMovies(base, "latest").slice(0, count);
}

export function getHotMovies(count: number): Movie[] {
  return sortMovies(allMovies, "popular").slice(0, count);
}

export function getRelatedMovies(movie: Movie, count: number): Movie[] {
  const scored = allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.type === movie.type) score += 2;
      if (item.region === movie.region) score += 1;
      score += item.genres.filter((g) => movie.genres.includes(g)).length * 3;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);
  return scored.slice(0, count).map((entry) => entry.item);
}

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getLatestNews(count: number): NewsItem[] {
  return [...allNews]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, count);
}

export function getRelatedNews(movie: Movie, count: number): NewsItem[] {
  const direct = allNews.filter((item) => item.relatedMovieIds.includes(movie.id));
  const rest = allNews.filter((item) => !item.relatedMovieIds.includes(movie.id));
  return [...direct, ...rest].slice(0, count);
}

export function getMoviesForNews(news: NewsItem): Movie[] {
  return news.relatedMovieIds
    .map((id) => getMovieById(id))
    .filter((item): item is Movie => Boolean(item));
}